import axios from 'axios';

const API_URL = process.env.REACT_APP_STRAPI_API_URL || 'https://www.eatmeatapp.com';

const authHeaders = () => {
  const jwt = localStorage.getItem('jwt');
  return jwt ? { Authorization: `Bearer ${jwt}` } : {};
};

// Categories
export const fetchCommunityCategories = async () => {
  const response = await axios.get(`${API_URL}/api/community-categories`, {
    params: { sort: 'sortOrder:asc', 'filters[is_active][$eq]': true },
  });
  return response.data.data || [];
};

// Feed
export const fetchCommunityFeed = async (page = 1, pageSize = 20, category?: number) => {
  const params: any = { page, pageSize };
  if (category) params.category = category;
  const response = await axios.get(`${API_URL}/api/community-posts/feed`, {
    params,
    headers: authHeaders(),
  });
  return response.data;
};

// Single post with comments
export const fetchPost = async (id: string) => {
  const response = await axios.get(`${API_URL}/api/community-posts/${id}`, {
    params: { populate: 'user,images,category,mentioned_recipes,mentioned_custom_recipes' },
    headers: authHeaders(),
  });
  return response.data.data;
};

// Fetch comments for a post
export const fetchComments = async (postId: number, page = 1) => {
  const response = await axios.get(`${API_URL}/api/community-comments`, {
    params: {
      'filters[post][id][$eq]': postId,
      'filters[is_hidden][$eq]': false,
      populate: 'user',
      sort: 'createdAt:asc',
      'pagination[page]': page,
      'pagination[pageSize]': 50,
    },
    headers: authHeaders(),
  });
  return response.data;
};

// Create post
export const createPost = async (data: { content: string; category?: number; images?: number[]; mentioned_recipes?: string[]; mentioned_custom_recipes?: string[] }) => {
  const userId = localStorage.getItem('userId');
  const response = await axios.post(`${API_URL}/api/community-posts`, {
    data: { ...data, user: userId },
  }, { headers: { ...authHeaders(), 'Content-Type': 'application/json' } });
  return response.data;
};

// Upload image
export const uploadImage = async (file: File) => {
  const formData = new FormData();
  formData.append('files', file);
  const response = await axios.post(`${API_URL}/api/upload`, formData, {
    headers: { ...authHeaders() },
  });
  return response.data[0];
};

// React to post
export const toggleReaction = async (postId: number, reactionType: string) => {
  const response = await axios.post(`${API_URL}/api/community-posts/${postId}/react`, {
    reaction_type: reactionType,
  }, { headers: authHeaders() });
  return response.data;
};

// Add comment
export const addComment = async (postId: number, content: string) => {
  const response = await axios.post(`${API_URL}/api/community-posts/${postId}/comment`, {
    content,
  }, { headers: authHeaders() });
  return response.data;
};

// Delete comment
export const deleteComment = async (commentId: number) => {
  const response = await axios.delete(`${API_URL}/api/community-comments/${commentId}`, {
    headers: authHeaders(),
  });
  return response.data;
};

// Report post
export const reportPost = async (postId: number, reason: string) => {
  const response = await axios.post(`${API_URL}/api/community-posts/${postId}/report`, {
    reason,
  }, { headers: authHeaders() });
  return response.data;
};

// Report comment
export const reportComment = async (commentId: number, reason?: string) => {
  const response = await axios.post(`${API_URL}/api/community-comments/${commentId}/report`, {
    reason,
  }, { headers: authHeaders() });
  return response.data;
};

// Moderator: approve / hide / unhide / delete post
export const approvePost = async (postId: number) => {
  const response = await axios.post(`${API_URL}/api/community-posts/${postId}/approve`, {}, { headers: authHeaders() });
  return response.data;
};
export const hidePost = async (postId: number) => {
  const response = await axios.post(`${API_URL}/api/community-posts/${postId}/hide`, {}, { headers: authHeaders() });
  return response.data;
};
export const unhidePost = async (postId: number) => {
  const response = await axios.post(`${API_URL}/api/community-posts/${postId}/unhide`, {}, { headers: authHeaders() });
  return response.data;
};
export const moderatorDeletePost = async (postId: number) => {
  const response = await axios.post(`${API_URL}/api/community-posts/${postId}/moderator-delete`, {}, { headers: authHeaders() });
  return response.data;
};

// Pending review queue (moderator only) — uses custom endpoint that returns a flat shape
export const fetchPendingPosts = async (page = 1, pageSize = 20) => {
  const response = await axios.get(`${API_URL}/api/community-posts/pending-queue`, {
    params: { page, pageSize },
    headers: authHeaders(),
  });
  return response.data;
};

// Current user (used to compute isModerator)
export interface CurrentUser {
  id: number;
  username: string;
  email: string;
  community_badge?: 'member' | 'moderator' | 'expert' | 'ambassador' | 'admin';
  is_community_moderator?: boolean;
}

export const fetchMe = async (): Promise<CurrentUser | null> => {
  if (!localStorage.getItem('jwt')) return null;
  try {
    const response = await axios.get(`${API_URL}/api/users/me`, {
      params: { populate: 'role' },
      headers: authHeaders(),
    });
    return response.data;
  } catch {
    return null;
  }
};

const MODERATOR_BADGES = ['admin', 'moderator', 'ambassador', 'expert'];
export const isModerator = (user: CurrentUser | null | undefined): boolean => {
  if (!user) return false;
  if (user.is_community_moderator === true) return true;
  return !!user.community_badge && MODERATOR_BADGES.includes(user.community_badge);
};
